/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import axios from "axios";
import { AUTH_EVENTS, getClientSessionTokenKey } from "./constants/constants";

let refreshTokenPromise = null;

export const axiosConfig = {
  baseURL: null,
  companyLogin: null,
  token: null,
  apiKey: null,
};

export const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

export const updateAxiosConfig = ({ baseURL, companyLogin, apiKey }) => {
  axiosConfig.baseURL = baseURL;
  axiosConfig.companyLogin = companyLogin;
  axiosConfig.apiKey = apiKey;

  axiosInstance.defaults.baseURL = baseURL;
};

export const updateAxiosHeaders = (headers) => {
  axiosInstance.defaults.headers.common = {
    ...axiosInstance.defaults.headers.common,
    ...headers,
  };
};

async function refreshToken() {
  const response = await axiosInstance.post("/public/auth/token", {
    company: axiosConfig.companyLogin,
    key: axiosConfig.apiKey,
  });

  return response.data.token;
}

const handleTokenExpiration = async (originalRequest) => {
  const newToken = await refreshToken();

  axiosInstance.defaults.headers.common["X-Token"] = newToken;
  axiosInstance.defaults.headers.common["X-Company-Login"] = axiosConfig.companyLogin;

  originalRequest.headers["X-Token"] = newToken;
  originalRequest.headers["X-Company-Login"] = axiosConfig.companyLogin;
};

const readStoredClientToken = (storage, storageKey) => {
  if (!storageKey) return null;
  try {
    const raw = storage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const isStoredClientToken = (token, companyLogin) => {
  if (!token || !companyLogin) return false;
  const key = getClientSessionTokenKey(companyLogin);

  return token === readStoredClientToken(localStorage, key) || token === readStoredClientToken(sessionStorage, key);
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const errorCode = error.response?.data?.code;
    const httpStatus = error.response?.status;
    const requestToken = originalRequest?.headers?.["X-Token"];
    // Prefer the request's own X-Company-Login over the singleton config — pairs
    // the storage-key check to the request that actually went out, even if a
    // sibling widget mutated axiosConfig in the meantime.
    const requestCompanyLogin =
      originalRequest?.headers?.["X-Company-Login"] || axiosConfig.companyLogin;
    const requestUsedStoredToken = isStoredClientToken(requestToken, requestCompanyLogin);
    const isExpired = errorCode === 419 || httpStatus === 419;
    const isUnauthorizedStoredToken =
      (errorCode === 401 || httpStatus === 401) && requestUsedStoredToken;
    const tokenIsDead = isExpired || isUnauthorizedStoredToken;

    if (tokenIsDead) {
      axiosInstance.defaults.headers.common["X-Token"] = null;
      axiosInstance.defaults.headers.common["X-Company-Login"] = null;

      if (requestUsedStoredToken) {
        const key = getClientSessionTokenKey(requestCompanyLogin);
        
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      }

      window.dispatchEvent(new CustomEvent(AUTH_EVENTS.SESSION_EXPIRED));

      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (!refreshTokenPromise) {
          refreshTokenPromise = handleTokenExpiration(originalRequest);
        }

        try {
          await refreshTokenPromise;
          refreshTokenPromise = null;
        } catch (refreshError) {
          refreshTokenPromise = null;
          throw error;
        }
      }
    }

    throw error;
  }
);

export default axiosInstance;
